/**
 * GET /api/v1/agent/inbox/conversations/:sessionId/customer-history
 * Prior sessions (same email) + AI summary (requires tenant OpenAI key on server).
 */
import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { humanAgentInboxUrl, inboxWorkspaceHeaders, isHumanAgentInboxLive } from "@/lib/inboxLiveConfig";

function resolveInboxRequestUrl(path) {
  const s = humanAgentInboxUrl(path);
  if (/^https?:\/\//i.test(s)) return new URL(s);
  return new URL(s, (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
}

/**
 * @param {{
 *   sessionId: string,
 *   workspaceId?: string | null,
 *   sessionWorkspaceId?: string | null,
 *   scope?: "central",
 * }} params
 * @returns {Promise<Record<string, unknown>>}
 */
export async function fetchInboxCustomerHistory(params) {
  if (!isHumanAgentInboxLive()) {
    throw new Error("inbox customer history is only available in live inbox mode");
  }
  const sessionId = String(params.sessionId ?? "").trim();
  if (!sessionId) throw new Error("sessionId required");

  const central = params.scope === "central";
  const sessionWs =
    params.sessionWorkspaceId != null && String(params.sessionWorkspaceId).trim()
      ? String(params.sessionWorkspaceId).trim()
      : null;

  if (!central && (!params.workspaceId || !String(params.workspaceId).trim())) {
    throw new Error("workspace id required for customer history");
  }

  const path = `/inbox/conversations/${encodeURIComponent(sessionId)}/customer-history`;
  const buildRequest = () => {
    const url = resolveInboxRequestUrl(path);
    if (central) {
      url.searchParams.set("scope", "central");
    }
    return fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...getBearerAuthHeaders(),
        ...inboxWorkspaceHeaders(central ? sessionWs : (params.workspaceId ?? null)),
      },
    });
  };
  const res = await fetchWithAuthRetry(buildRequest);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
